// src/products/equityManager/utils/capTableCalculations.js
// Utility functions for cap table calculations and round summaries

/**
 * Groups share issuances by round (either explicit round or date-based grouping)
 * @param {Array} shareIssuances - Array of share issuance objects
 * @returns {Array} Array of round objects with issuances grouped
 */
export const groupIssuancesByRound = (shareIssuances) => {
    if (!shareIssuances || shareIssuances.length === 0) {
        return [];
    }

    const issuancesByRound = {};

    shareIssuances.forEach(issuance => {
        // Use 'round' column if it exists, otherwise use date-based grouping
        let roundKey;
        if (issuance.round) {
            roundKey = issuance.round;
        } else {
            // Fallback: group by similar dates (within 30 days)
            const issueDate = new Date(issuance.issue_date);
            roundKey = `Round ${Math.floor(issueDate.getTime() / (30 * 24 * 60 * 60 * 1000))}`;
        }

        if (!issuancesByRound[roundKey]) {
            issuancesByRound[roundKey] = {
                roundNumber: issuance.round || roundKey,
                roundName: issuance.round || `Round ${roundKey}`,
                issuances: [],
                earliestDate: issuance.issue_date
            };
        }

        issuancesByRound[roundKey].issuances.push(issuance);
        
        // Track earliest date for the round
        if (new Date(issuance.issue_date) < new Date(issuancesByRound[roundKey].earliestDate)) {
            issuancesByRound[roundKey].earliestDate = issuance.issue_date;
        }
    });

    // Convert to array and sort by round number or date
    return Object.values(issuancesByRound).sort((a, b) => {
        // Try to sort by round number first
        if (a.roundNumber && b.roundNumber && !isNaN(a.roundNumber) && !isNaN(b.roundNumber)) {
            return parseInt(a.roundNumber) - parseInt(b.roundNumber);
        }
        // Fallback to date sorting
        return new Date(a.earliestDate) - new Date(b.earliestDate);
    });
};

/**
 * Processes a single issuance and updates cumulative holdings
 * @param {Object} issuance - Single share issuance object
 * @param {Object} cumulativeHoldings - Current cumulative holdings object
 * @param {string} roundNumber - Current round number
 * @returns {Object} Updated round totals
 */
export const processIssuance = (issuance, cumulativeHoldings, roundNumber) => {
    const shareholderName = issuance.shareholder_name || `Shareholder ${issuance.shareholder_id}`;
    const shares = parseInt(issuance.shares) || 0;
    const price = parseFloat(issuance.price_per_share) || 0;
    const value = shares * price;

    if (!cumulativeHoldings[shareholderName]) {
        cumulativeHoldings[shareholderName] = {
            totalShares: 0,
            totalValue: 0,
            shareClass: issuance.share_class || 'Common',
            holdings: []
        };
    }

    cumulativeHoldings[shareholderName].totalShares += shares;
    cumulativeHoldings[shareholderName].totalValue += value;
    cumulativeHoldings[shareholderName].holdings.push({
        roundNumber: roundNumber,
        shares: shares,
        price: price,
        value: value,
        shareClass: issuance.share_class || 'Common'
    });

    return { shares, value, price };
};

/**
 * Calculates cap table snapshot for a round
 * @param {Object} cumulativeHoldings - Current cumulative holdings
 * @param {number} totalSharesOutstanding - Total shares outstanding
 * @param {number} highestPrice - Highest price in the round
 * @returns {Array} Cap table array sorted by share count
 */
export const calculateCapTableSnapshot = (cumulativeHoldings, totalSharesOutstanding, highestPrice) => {
    return Object.entries(cumulativeHoldings).map(([name, data]) => ({
        shareholderName: name,
        totalShares: data.totalShares,
        percentage: totalSharesOutstanding > 0 ? (data.totalShares / totalSharesOutstanding * 100) : 0,
        totalValue: data.totalValue,
        valueAtHighestPrice: data.totalShares * highestPrice,
        shareClass: data.shareClass,
        holdings: data.holdings
    })).sort((a, b) => b.totalShares - a.totalShares);
};

/**
 * Calculates round summary data including cap table evolution
 * @param {Array} rounds - Array of round objects with issuances
 * @returns {Array} Array of round summary objects with cap table data
 */
export const calculateRoundSummaries = (rounds) => {
    const roundSummaries = [];
    let cumulativeHoldings = {};

    rounds.forEach((round) => {
        // Calculate round totals
        let roundTotalShares = 0;
        let roundTotalValue = 0;
        let highestPrice = 0;

        // Process this round's issuances
        round.issuances.forEach(issuance => {
            const { shares, value, price } = processIssuance(issuance, cumulativeHoldings, round.roundNumber);
            roundTotalShares += shares;
            roundTotalValue += value;
            highestPrice = Math.max(highestPrice, price);
        });

        // Calculate total shares outstanding after this round
        const totalSharesOutstanding = Object.values(cumulativeHoldings)
            .reduce((sum, holder) => sum + holder.totalShares, 0);

        // Create cap table snapshot for this round
        const capTable = calculateCapTableSnapshot(cumulativeHoldings, totalSharesOutstanding, highestPrice);

        roundSummaries.push({
            ...round,
            capTable,
            roundTotalShares,
            roundTotalValue,
            totalSharesOutstanding,
            highestPrice,
            averagePrice: roundTotalShares > 0 ? roundTotalValue / roundTotalShares : 0
        });
    });

    return roundSummaries;
};

/**
 * Main function to calculate complete round summary from share issuances
 * @param {Array} shareIssuances - Array of share issuance objects
 * @returns {Array} Complete round summary data
 */
export const calculateCompleteRoundSummary = (shareIssuances) => {
    if (!shareIssuances || shareIssuances.length === 0) {
        return [];
    }

    const rounds = groupIssuancesByRound(shareIssuances);
    return calculateRoundSummaries(rounds);
};

/**
 * Prepares data for dilution chart visualization
 * @param {Array} roundData - Array of round summary objects
 * @returns {Array} Chart data formatted for recharts
 */
export const prepareDilutionChartData = (roundData) => {
    if (!roundData || roundData.length === 0) return [];

    // Get all unique shareholders across all rounds
    const allShareholders = new Set();
    roundData.forEach(round => {
        round.capTable.forEach(holder => {
            allShareholders.add(holder.shareholderName);
        });
    });

    // Create chart data with percentage ownership for each shareholder in each round
    return roundData.map((round) => {
        const chartDataPoint = {
            round: `Round ${round.roundNumber}`,
            roundNumber: round.roundNumber
        };

        // Initialize all shareholders with 0%
        allShareholders.forEach(shareholder => {
            chartDataPoint[shareholder] = 0;
        });

        // Set actual percentages for shareholders in this round
        round.capTable.forEach(holder => {
            chartDataPoint[holder.shareholderName] = parseFloat(holder.percentage.toFixed(2));
        });

        return chartDataPoint;
    });
};

/**
 * Generates color mapping for shareholders in charts
 * @param {Array} roundData - Array of round summary objects
 * @returns {Object} Object mapping shareholder names to colors
 */
export const generateShareholderColors = (roundData) => {
    if (!roundData || roundData.length === 0) return {};

    const allShareholders = new Set();
    roundData.forEach(round => {
        round.capTable.forEach(holder => {
            allShareholders.add(holder.shareholderName);
        });
    });

    const colors = [
        '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
        '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
        '#06B6D4', '#F43F5E', '#8B5A2B', '#7C3AED', '#059669',
        '#DC2626', '#2563EB', '#CA8A04', '#9333EA', '#0891B2'
    ];

    const shareholderColors = {};
    Array.from(allShareholders).forEach((shareholder, index) => {
        shareholderColors[shareholder] = colors[index % colors.length];
    });

    return shareholderColors;
};

/**
 * Calculates overview statistics from round data
 * @param {Array} roundData - Array of round summary objects
 * @returns {Object} Overview statistics object
 */
export const calculateOverviewStats = (roundData) => {
    if (!roundData || roundData.length === 0) {
        return {
            totalRounds: 0,
            totalShareholders: 0,
            totalSharesOutstanding: 0,
            totalCapitalRaised: 0
        };
    }

    const latestRound = roundData[roundData.length - 1];
    
    return {
        totalRounds: roundData.length,
        totalShareholders: latestRound.capTable.length,
        totalSharesOutstanding: latestRound.totalSharesOutstanding,
        totalCapitalRaised: roundData.reduce((sum, round) => sum + round.roundTotalValue, 0)
    };
};
